import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';


const OpenSourceSection = () => {
  const [selectedRepo, setSelectedRepo] = useState(null);

  const repositories = [
    {
      id: 1,
      name: 'react-component-library',
      description: 'A comprehensive React component library with TypeScript support, Storybook documentation, and automated testing.',
      language: 'TypeScript',
      stars: 1247,
      forks: 89,
      issues: 12,
      lastCommit: '2 days ago',
      topics: ['react', 'typescript', 'components', 'ui-library'],
      status: 'active',
      contributors: 15,
      license: 'MIT',
      url: 'https://github.com/username/react-component-library',
      commits: [
        {
          message: 'feat: Add dark mode support to Button component',
          author: 'John Doe',
          date: '2 days ago',
          hash: 'a1b2c3d'
        },
        {
          message: 'fix: Resolve accessibility issues in Modal component',
          author: 'John Doe',
          date: '4 days ago',
          hash: 'e4f5g6h'
        },
        {
          message: 'docs: Update Storybook examples for Input component',
          author: 'Jane Smith',
          date: '1 week ago',
          hash: 'i7j8k9l'
        }
      ]
    },
    {
      id: 2,
      name: 'api-performance-optimizer',
      description: 'Node.js middleware for API performance optimization with caching, rate limiting, and response compression.',
      language: 'JavaScript',
      stars: 892,
      forks: 67,
      issues: 8,
      lastCommit: '5 days ago',
      topics: ['nodejs', 'api', 'performance', 'middleware'],
      status: 'active',
      contributors: 8,
      license: 'Apache-2.0',
      url: 'https://github.com/username/api-performance-optimizer',
      commits: [
        {
          message: 'perf: Improve cache hit ratio by 25%',
          author: 'John Doe',
          date: '5 days ago',
          hash: 'm1n2o3p'
        },
        {
          message: 'feat: Add Redis cluster support',
          author: 'Mike Johnson',
          date: '1 week ago',
          hash: 'q4r5s6t'
        }
      ]
    },
    {
      id: 3,
      name: 'css-animation-toolkit',
      description: 'A collection of smooth, performant CSS animations and transitions for modern web applications.',
      language: 'CSS',
      stars: 2156,
      forks: 234,
      issues: 3,
      lastCommit: '1 week ago',
      topics: ['css', 'animations', 'transitions', 'web-design'],
      status: 'maintained',
      contributors: 23,
      license: 'MIT',
      url: 'https://github.com/username/css-animation-toolkit',
      commits: [
        {
          message: 'feat: Add new slide-in animations',
          author: 'Sarah Wilson',
          date: '1 week ago',
          hash: 'u7v8w9x'
        },
        {
          message: 'fix: Improve animation performance on mobile',
          author: 'John Doe',
          date: '2 weeks ago',
          hash: 'y1z2a3b'
        }
      ]
    },
    {
      id: 4,
      name: 'data-visualization-charts',
      description: 'Interactive data visualization library built with D3.js and React, featuring customizable charts and graphs.',
      language: 'JavaScript',
      stars: 567,
      forks: 45,
      issues: 15,
      lastCommit: '3 days ago',
      topics: ['d3js', 'react', 'charts', 'data-visualization'],
      status: 'active',
      contributors: 6,
      license: 'MIT',
      url: 'https://github.com/username/data-visualization-charts',
      commits: [
        {
          message: 'feat: Add real-time data streaming support',
          author: 'John Doe',
          date: '3 days ago',
          hash: 'c4d5e6f'
        }
      ]
    },
    {
      id: 5,
      name: 'webpack-optimization-plugin',
      description: 'Webpack plugin for advanced bundle optimization, code splitting, and performance analysis.',
      language: 'JavaScript',
      stars: 334,
      forks: 28,
      issues: 6,
      lastCommit: '1 month ago',
      topics: ['webpack', 'optimization', 'bundling', 'performance'],
      status: 'maintained',
      contributors: 4,
      license: 'MIT',
      url: 'https://github.com/username/webpack-optimization-plugin',
      commits: [
        {
          message: 'feat: Support for Webpack 5 module federation',
          author: 'John Doe',
          date: '1 month ago',
          hash: 'g7h8i9j'
        }
      ]
    },
    {
      id: 6,
      name: 'algorithm-visualizer-web',
      description: 'Web-based algorithm visualization tool for learning data structures and algorithms with interactive examples.',
      language: 'TypeScript',
      stars: 1789,
      forks: 156,
      issues: 9,
      lastCommit: '6 days ago',
      topics: ['algorithms', 'data-structures', 'education', 'visualization'],
      status: 'active',
      contributors: 12,
      license: 'GPL-3.0',
      url: 'https://github.com/username/algorithm-visualizer-web',
      commits: [
        {
          message: 'feat: Add graph traversal algorithms',
          author: 'Alex Chen',
          date: '6 days ago',
          hash: 'k1l2m3n'
        }
      ]
    }
  ];

  const getLanguageColor = (language) => {
    const colors = {
      'TypeScript': 'bg-blue-500',
      'JavaScript': 'bg-yellow-500',
      'CSS': 'bg-purple-500',
      'Python': 'bg-green-500',
      'Java': 'bg-red-500'
    };
    return colors?.[language] || 'bg-gray-500';
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': 'text-green-600 bg-green-100',
      'maintained': 'text-blue-600 bg-blue-100',
      'archived': 'text-gray-600 bg-gray-100'
    };
    return colors?.[status] || 'text-gray-600 bg-gray-100';
  };

  const totalStats = {
    repositories: repositories?.length,
    stars: repositories?.reduce((sum, repo) => sum + repo?.stars, 0),
    forks: repositories?.reduce((sum, repo) => sum + repo?.forks, 0),
    contributors: repositories?.reduce((sum, repo) => sum + repo?.contributors, 0)
  };

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-background rounded-lg border border-border p-4 text-center">
          <div className="text-2xl font-bold text-accent">{totalStats?.repositories}</div>
          <div className="text-sm text-text-secondary">Repositories</div>
        </div>
        <div className="bg-background rounded-lg border border-border p-4 text-center">
          <div className="text-2xl font-bold text-warning">{totalStats?.stars?.toLocaleString()}</div>
          <div className="text-sm text-text-secondary">Total Stars</div>
        </div>
        <div className="bg-background rounded-lg border border-border p-4 text-center">
          <div className="text-2xl font-bold text-success">{totalStats?.forks}</div>
          <div className="text-sm text-text-secondary">Total Forks</div>
        </div>
        <div className="bg-background rounded-lg border border-border p-4 text-center">
          <div className="text-2xl font-bold text-primary">{totalStats?.contributors}</div>
          <div className="text-sm text-text-secondary">Contributors</div>
        </div>
      </div>
      {/* Repository Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {repositories?.map((repo) => (
          <div
            key={repo?.id}
            className="bg-background rounded-lg border border-border p-6 hover:shadow-medium transition-all duration-300 cursor-pointer"
            onClick={() => setSelectedRepo(selectedRepo === repo?.id ? null : repo?.id)}
          >
            {/* Repository Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Icon name="GitBranch" size={20} className="text-accent" />
                <h3 className="font-semibold text-primary hover:text-accent transition-colors">
                  {repo?.name}
                </h3>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(repo?.status)}`}>
                  {repo?.status}
                </span>
                <Button
                  variant="ghost"
                  size="xs"
                  iconName="ExternalLink"
                  onClick={(e) => {
                    e?.stopPropagation();
                    window.open(repo?.url, '_blank');
                  }}
                />
              </div>
            </div>

            {/* Description */}
            <p className="text-sm text-text-secondary mb-4 line-clamp-2">
              {repo?.description}
            </p>

            {/* Language and Stats */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${getLanguageColor(repo?.language)}`}></div>
                <span className="text-sm text-text-secondary">{repo?.language}</span>
              </div>
              <div className="flex items-center space-x-4 text-sm text-text-secondary">
                <div className="flex items-center space-x-1">
                  <Icon name="Star" size={14} />
                  <span>{repo?.stars}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="GitFork" size={14} />
                  <span>{repo?.forks}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="AlertCircle" size={14} />
                  <span>{repo?.issues}</span>
                </div>
              </div>
            </div>

            {/* Topics */}
            <div className="flex flex-wrap gap-2 mb-4">
              {repo?.topics?.slice(0, 4)?.map((topic, index) => (
                <span
                  key={index}
                  className="px-2 py-1 text-xs bg-muted text-text-secondary rounded"
                >
                  {topic}
                </span>
              ))}
              {repo?.topics?.length > 4 && (
                <span className="px-2 py-1 text-xs bg-muted text-text-secondary rounded">
                  +{repo?.topics?.length - 4} more
                </span>
              )}
            </div>

            {/* Last Commit */}
            <div className="flex items-center justify-between text-xs text-text-secondary">
              <span>Updated {repo?.lastCommit}</span>
              <span>{repo?.license}</span>
            </div>

            {/* Expanded Details */}
            {selectedRepo === repo?.id && (
              <div className="mt-4 pt-4 border-t border-border space-y-4">
                <div>
                  <h4 className="font-medium mb-2 flex items-center space-x-2">
                    <Icon name="GitCommit" size={16} className="text-accent" />
                    <span>Recent Commits</span>
                  </h4>
                  <div className="space-y-2">
                    {repo?.commits?.map((commit, index) => (
                      <div key={index} className="flex items-start space-x-3 p-2 bg-muted/30 rounded">
                        <div className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0"></div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-primary truncate">
                            {commit?.message}
                          </p>
                          <div className="flex items-center space-x-2 text-xs text-text-secondary mt-1">
                            <span>{commit?.author}</span>
                            <span>•</span>
                            <span>{commit?.date}</span>
                            <span>•</span>
                            <code className="bg-muted px-1 rounded">{commit?.hash}</code>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <div className="flex items-center space-x-4 text-sm text-text-secondary">
                    <span>{repo?.contributors} contributors</span>
                    <span>•</span>
                    <span>{repo?.commits?.length}+ commits</span>
                  </div>
                  <Button
                    variant="outline"
                    size="xs"
                    iconName="Github"
                    iconPosition="left"
                    onClick={(e) => {
                      e?.stopPropagation();
                      window.open(repo?.url, '_blank');
                    }}
                  >
                    View on GitHub
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Contribution Activity */}
      <div className="bg-background rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
          <Icon name="Activity" size={20} className="text-accent" />
          <span>Contribution Activity</span>
        </h3>
        
        <div className="grid grid-cols-7 gap-1 mb-4">
          {Array.from({ length: 365 }, (_, i) => {
            const intensity = Math.random();
            let bgColor = 'bg-gray-100';
            if (intensity > 0.8) bgColor = 'bg-green-600';
            else if (intensity > 0.6) bgColor = 'bg-green-500';
            else if (intensity > 0.4) bgColor = 'bg-green-400';
            else if (intensity > 0.2) bgColor = 'bg-green-200';
            
            return (
              <div
                key={i}
                className={`w-3 h-3 rounded-sm ${bgColor}`}
                title={`${Math.floor(intensity * 10)} contributions`}
              />
            );
          })}
        </div>
        
        <div className="flex items-center justify-between text-sm text-text-secondary">
          <span>Less</span>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-gray-100 rounded-sm"></div>
            <div className="w-3 h-3 bg-green-200 rounded-sm"></div>
            <div className="w-3 h-3 bg-green-400 rounded-sm"></div>
            <div className="w-3 h-3 bg-green-500 rounded-sm"></div>
            <div className="w-3 h-3 bg-green-600 rounded-sm"></div>
          </div>
          <span>More</span>
        </div>
      </div>
      {/* Call to Action */}
      <div className="bg-gradient-to-r from-accent/10 to-primary/10 rounded-lg p-6 text-center">
        <h3 className="text-xl font-semibold mb-2">Interested in Collaboration?</h3>
        <p className="text-text-secondary mb-4">
          I'm always open to contributing to interesting projects and collaborating with fellow developers.
        </p>
        <div className="flex items-center justify-center space-x-4">
          <Button
            variant="default"
            iconName="Github"
            iconPosition="left"
            onClick={() => window.open('https://github.com/username', '_blank')}
            className="bg-accent hover:bg-hover text-accent-foreground"
          >
            Follow on GitHub
          </Button>
          <Button
            variant="outline"
            iconName="Mail"
            iconPosition="left"
            onClick={() => window.location.href = '/contact-opportunities'}
          >
            Get in Touch
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OpenSourceSection;